import React, { useState, useEffect } from 'react';
import { crmService } from '../../services/crmService';

// ─── Awards Intelligence Widget (Watering Hole) ────────────────────────────

function AwardItem({ award, onViewDetails }) {
  const formatAmount = (val) => {
    if (!val) return '—';
    if (val >= 1000000) return `$${(val / 1000000).toFixed(1)}M`;
    if (val >= 1000) return `$${(val / 1000).toFixed(0)}K`;
    return `$${val}`;
  };

  const getVerticalColor = (vertName) => {
    const colors = {
      'AI/ML': '#A78BFA',
      'Cyber Defense': '#34D399',
      'Autonomous Systems': '#60A5FA',
      'C2ISR': '#FBBF24',
      'RF/Sensors': '#FB7185',
      'Propulsion': '#06B6D4',
      'Space Systems': '#8B5CF6',
    };
    return colors[vertName] || '#22D3EE';
  };

  const primaryVertical = award.matched_verticals?.[0];

  return (
    <div
      onClick={() => onViewDetails?.(award)}
      style={{
        background: '#111827', border: '1px solid #1E2D45',
        borderRadius: 6, padding: 12, cursor: 'pointer',
        transition: 'all 0.2s',
        marginBottom: 8,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = '#22D3EE66';
        e.currentTarget.style.background = '#0F1928';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = '#1E2D45';
        e.currentTarget.style.background = '#111827';
      }}
    >
      {/* Header: Vendor & Amount */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
        <div>
          <div style={{ color: '#F1F5F9', fontWeight: 600, fontSize: 12 }}>
            {award.vendor_name}
          </div>
          {award.is_new_today && (
            <span style={{
              background: 'rgba(34, 211, 238, 0.2)', color: '#22D3EE',
              padding: '1px 4px', borderRadius: 2, fontSize: 8, fontWeight: 700, marginTop: 2, display: 'inline-block'
            }}>
              NEW
            </span>
          )}
        </div>
        <div style={{ color: '#10B981', fontWeight: 700, fontSize: 13, textAlign: 'right' }}>
          {formatAmount(award.award_amount_usd)}
        </div>
      </div>

      {/* Agency & Vertical */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <div style={{ color: '#64748B', fontSize: 10 }}>
          {award.award_agency}
        </div>
        {primaryVertical && (
          <span style={{
            background: getVerticalColor(primaryVertical.name) + '22',
            color: getVerticalColor(primaryVertical.name),
            border: `1px solid ${getVerticalColor(primaryVertical.name)}44`,
            padding: '1px 6px', borderRadius: 3, fontSize: 9, fontWeight: 600
          }}>
            {primaryVertical.name}
          </span>
        )}
      </div>

      {/* Date */}
      <div style={{ color: '#4B5563', fontSize: 9 }}>
        {new Date(award.award_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
      </div>
    </div>
  );
}

export default function AwardsWidget() {
  const [awards, setAwards] = useState([]);
  const [digest, setDigest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedAward, setSelectedAward] = useState(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [awardData, digestData] = await Promise.all([
          crmService.getGovAwards({ days_back: 7, limit: 5 }),
          crmService.getAwardDigest(),
        ]);
        setAwards(awardData.awards || []);
        setDigest(digestData.digest);
      } catch (e) {
        console.error('Load awards error:', e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const totalValue = awards.reduce((sum, a) => sum + (a.award_amount_usd || 0), 0);
  const formatValue = (val) => {
    if (val >= 1000000) return `$${(val / 1000000).toFixed(1)}M`;
    if (val >= 1000) return `$${(val / 1000).toFixed(0)}K`;
    return `$${val}`;
  };

  if (loading) {
    return (
      <div style={{
        background: '#111827', border: '1px solid #1E2D45', borderRadius: 8,
        padding: 16, color: '#64748B', textAlign: 'center', fontSize: 11
      }}>
        Loading awards…
      </div>
    );
  }

  return (
    <div style={{
      background: '#111827', border: '1px solid #1E2D45', borderRadius: 8,
      overflow: 'hidden'
    }}>
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #0D1F3C 0%, #1a2f52 100%)',
        padding: '16px',
        borderBottom: '1px solid #1E2D45'
      }}>
        <h3 style={{ margin: 0, color: '#22D3EE', fontSize: 14, fontWeight: 700, marginBottom: 10 }}>
          🏆 DoD Awards This Week
        </h3>

        {/* Stats Grid */}
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8
        }}>
          <div>
            <div style={{ color: '#64748B', fontSize: 9 }}>Awards</div>
            <div style={{ color: '#F1F5F9', fontSize: 14, fontWeight: 700 }}>
              {awards.length}
            </div>
          </div>
          <div>
            <div style={{ color: '#64748B', fontSize: 9 }}>Total Value</div>
            <div style={{ color: '#10B981', fontSize: 14, fontWeight: 700 }}>
              {formatValue(totalValue)}
            </div>
          </div>
          <div>
            <div style={{ color: '#64748B', fontSize: 9 }}>Vendors</div>
            <div style={{ color: '#A78BFA', fontSize: 14, fontWeight: 700 }}>
              {new Set(awards.map(a => a.vendor_name)).size}
            </div>
          </div>
        </div>
      </div>

      {/* Awards List */}
      <div style={{ padding: 12, maxHeight: 300, overflowY: 'auto' }}>
        {awards.length === 0 ? (
          <div style={{ color: '#4B5563', textAlign: 'center', padding: '20px 10px', fontSize: 11 }}>
            No awards in the past 7 days matching your verticals
          </div>
        ) : (
          awards.map(award => (
            <AwardItem
              key={award.id}
              award={award}
              onViewDetails={() => setSelectedAward(award)}
            />
          ))
        )}
      </div>

      {/* Footer: View All Link */}
      <div style={{
        padding: '12px 16px',
        borderTop: '1px solid #1E2D45',
        textAlign: 'center'
      }}>
        <a href="/crm/watering-hole/awards" style={{
          color: '#22D3EE', fontSize: 11, fontWeight: 600, textDecoration: 'none'
        }}>
          View All Awards Intelligence →
        </a>
      </div>

      {/* Detail Overlay */}
      {selectedAward && (
        <div
          onClick={() => setSelectedAward(null)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#111827', border: '1px solid #1E2D45', borderRadius: 8,
              padding: 20, maxWidth: 500, width: '90vw'
            }}
          >
            <h3 style={{ margin: '0 0 12px', color: '#F1F5F9', fontSize: 14, fontWeight: 700 }}>
              {selectedAward.vendor_name}
            </h3>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12, marginBottom: 16 }}>
              <div>
                <div style={{ color: '#64748B', fontSize: 10, marginBottom: 4 }}>AWARD AMOUNT</div>
                <div style={{ color: '#10B981', fontSize: 13, fontWeight: 700 }}>
                  ${(selectedAward.award_amount_usd / 1000000).toFixed(1)}M
                </div>
              </div>
              <div>
                <div style={{ color: '#64748B', fontSize: 10, marginBottom: 4 }}>AWARDING AGENCY</div>
                <div style={{ color: '#F1F5F9', fontSize: 11 }}>
                  {selectedAward.award_agency}
                </div>
              </div>
              <div>
                <div style={{ color: '#64748B', fontSize: 10, marginBottom: 4 }}>AWARD DATE</div>
                <div style={{ color: '#94A3B8', fontSize: 11 }}>
                  {new Date(selectedAward.award_date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                </div>
              </div>
              <div>
                <div style={{ color: '#64748B', fontSize: 10, marginBottom: 4 }}>CONTRACT TYPE</div>
                <div style={{ color: '#94A3B8', fontSize: 11 }}>
                  {selectedAward.contract_type || '—'}
                </div>
              </div>
            </div>

            {selectedAward.contract_description && (
              <div style={{ marginBottom: 16, borderTop: '1px solid #1E2D45', paddingTop: 12 }}>
                <div style={{ color: '#64748B', fontSize: 10, marginBottom: 6 }}>DESCRIPTION</div>
                <p style={{ margin: 0, color: '#94A3B8', fontSize: 11, lineHeight: 1.5 }}>
                  {selectedAward.contract_description}
                </p>
              </div>
            )}

            {selectedAward.matched_verticals?.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ color: '#64748B', fontSize: 10, marginBottom: 6 }}>TECHNOLOGY AREAS</div>
                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                  {selectedAward.matched_verticals.map(v => (
                    <span key={v.id} style={{
                      background: '#22D3EE11', color: '#22D3EE', border: '1px solid #22D3EE33',
                      borderRadius: 4, padding: '2px 6px', fontSize: 9, fontWeight: 600
                    }}>
                      {v.name}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div style={{ display: 'flex', gap: 8 }}>
              <button
                onClick={() => setSelectedAward(null)}
                style={{
                  flex: 1, padding: '8px 12px', background: 'transparent', border: '1px solid #374151',
                  color: '#94A3B8', borderRadius: 4, cursor: 'pointer', fontSize: 11, fontWeight: 600
                }}
              >
                Close
              </button>
              {selectedAward.source_url && (
                <a
                  href={selectedAward.source_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    flex: 1, padding: '8px 12px', background: '#22D3EE', border: 'none',
                    color: '#0A0F1C', borderRadius: 4, cursor: 'pointer', fontSize: 11, fontWeight: 600,
                    textDecoration: 'none', textAlign: 'center'
                  }}
                >
                  View Source ↗
                </a>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
